'use client';
/* eslint-disable sonarjs/cognitive-complexity */
import { type Brand } from '@curology/ui-components-web/brand';
import { Button } from '@curology/ui-components-web/react';
import { Checkbox } from '@curology/ui-components-web/react/Checkbox';
import { Link } from '@curology/ui-components-web/react/Link';
import { TextInput } from '@curology/ui-components-web/react/TextInput';
import React, { useEffect, useState } from 'react';

import { useFlashMessages } from '@/atoms/flashMessages/useFlashMessages';
import COOKIE_KEYS from '@/consts/cookieKeys';
import { SITE_URL } from '@/consts/environmental';
import {
  type TreatmentCategory,
  TreatmentCategoryIds,
} from '@/consts/treatmentCategories';
import { OffersService } from '@/services/offersService/client';
import { RegistrationService } from '@/services/registrationService/client';
import { editSessionBag } from '@/services/visitsService/client';
import { AnalyticsEvent, FlowTypes, trackEvent } from '@/utils/analytics';
import cookie from '@/utils/cookies/client';
import { canonicalPath } from '@/utils/metrics/pages';
import { urls } from '@/utils/urls';

import { SignupFlowRedirectModal } from './SignupFlowRedirectModal';
import { useNavigation } from '../../../hooks/useNavigation';

interface EventHandlerEvent {
  target: { value: React.SetStateAction<string> };
}

const acceptedTOSCheckboxLabel = () => {
  return (
    <React.Fragment>
      I agree to the{' '}
      <Link href={`${SITE_URL}/terms`} target="_blank">
        Terms of Service
      </Link>
      ,{' '}
      <Link href={`${SITE_URL}/privacy`} target="_blank">
        Privacy Policy
      </Link>{' '}
      and{' '}
      <Link href={`${SITE_URL}/telehealth-consent`} target="_blank">
        Telehealth Consent
      </Link>
      .
    </React.Fragment>
  );
};

export const CreateAccountForm = ({
  brand,
  offerId,
  treatmentCategory,
  uuid,
}: {
  brand: Brand;
  offerId: number;
  treatmentCategory: TreatmentCategory;
  uuid: string;
}) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [acceptedTOS, setAcceptedTOS] = useState(false);
  const [isOfAge, setIsOfAge] = useState(false);
  const [canSubmit, setCanSubmit] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showRedirectModal, setShowRedirectModal] = useState(false);
  const [redirectUrl, setRedirectUrl] = useState('');
  const [pendingErrors, setPendingErrors] = useState<string[]>([]);

  const flashMessages = useFlashMessages();
  const { onNext } = useNavigation();

  useEffect(() => {
    setCanSubmit(
      email.trim() !== '' && password.trim() !== '' && acceptedTOS && isOfAge,
    );
  }, [email, password, acceptedTOS, isOfAge]);

  useEffect(() => {
    if (pendingErrors.length > 0 && !showRedirectModal) {
      pendingErrors.forEach((errorMessage) => {
        flashMessages.error({
          title: 'Sorry!',
          message: String(errorMessage),
        });
      });
      setPendingErrors([]);
    }
  }, [pendingErrors, showRedirectModal, flashMessages]);

  const submitForm = async () => {
    setIsSubmitting(true);

    try {
      trackEvent(AnalyticsEvent.ELIGIBILITY_SUBMITTED, {
        brand,
        flow: FlowTypes.PDP_CHECKOUT,
        funnelId: cookie.get(COOKIE_KEYS.funnelId),
        path: canonicalPath(),
        treatmentCategories: treatmentCategory,
        treatmentCategoryIds: TreatmentCategoryIds[treatmentCategory!],
      });

      const {
        data: { user },
      } = await RegistrationService.register(
        {
          acceptedTOS,
          disableInstantBrandTransfer: true,
          email,
          password,
        },
        brand,
      );

      await editSessionBag({
        brand,
        sessionBag: {
          getDobOnLegalNamePage: true,
          hasCreatedAccount: true,
        },
        uuid,
      }).catch(() => {
        // editSessionBag failure is non-critical; the session may not be
        // fully established immediately after account creation
      });

      trackEvent(AnalyticsEvent.ACCOUNT_REGISTERED, {
        fbEventId: `account_registered_${user.id}`,
        flow: FlowTypes.PDP_CHECKOUT,
        treatmentCategories: treatmentCategory!,
        treatmentCategoryIds: TreatmentCategoryIds[treatmentCategory!],
      });

      await OffersService.updateUserOffer(offerId, brand);

      onNext();
    } catch (e: any) {
      const errors = e.response?.data.errors ?? {};
      const errorMessages = Object.values(errors).flat() as string[];

      if (errorMessages) {
        // Check to see if this user is trying to login instead of signing up.
        if (
          errorMessages.length === 1 &&
          errorMessages
            .at(0)
            ?.startsWith('If an account exists with this email')
        ) {
          try {
            // If we got an "If an account exists..." message, try logging in with the same credentials
            const response = await RegistrationService.login(
              { email, password },
              brand,
            );
            // If we succeeded, check if there's a redirect to an incomplete SUF
            if (response.status === 200) {
              const redirectPath = response.data.redirect;
              const shouldBeRedirectedToRegularSUF =
                response.data?.shouldBeRedirectedToRegularSUF;

              if (redirectPath && shouldBeRedirectedToRegularSUF) {
                setRedirectUrl(redirectPath);
                setShowRedirectModal(true);
              } else {
                await OffersService.updateUserOffer(offerId, brand);

                // For other redirects, proceed with the existing logic
                if (response.data.redirect) {
                  window.location.assign(response.data.redirect);
                  return;
                }
                const brandUrls = urls[brand];
                const redirectUrl =
                  'patientDashboard' in brandUrls.legacy
                    ? brandUrls.legacy.patientDashboard.manageSubscription
                        .customize
                    : brandUrls.legacy.dashboard.url;
                window.location.assign(redirectUrl);
                return;
              }
            }
          } catch (loginError) {
            // Catch the login error, if any, and let execution fall through to the
            // regular error handling flow.
          }
        }

        // Regular error flow.
        // Errors are displayed reactively via useEffect,
        // which suppresses them when the redirect modal is shown.
        setPendingErrors(errorMessages);
      } else {
        setPendingErrors(['A system error occurred. Please try again.']);
      }

      setIsSubmitting(false);

      trackEvent(AnalyticsEvent.REGISTER_FORM_ERROR, {
        backendErrors: errors,
        flow: FlowTypes.PDP_CHECKOUT,
      });
    }
  };

  return (
    <>
      <SignupFlowRedirectModal
        isOpen={showRedirectModal}
        redirectUrl={redirectUrl}
      />
      <form className="cur-mb-4">
        <div className="cur-mb-4">
          <TextInput
            disabled={isSubmitting}
            id="email"
            label="Email"
            onChange={(event: EventHandlerEvent) =>
              setEmail(event.target.value)
            }
            placeholder="janedoe@email.com"
            required
            value={email}
          />
        </div>
        <div className="cur-mb-4">
          <TextInput
            disabled={isSubmitting}
            id="password"
            label="Password"
            onChange={(event: EventHandlerEvent) =>
              setPassword(event.target.value)
            }
            placeholder="Create a password"
            required
            type="password"
            value={password}
          />
        </div>
        <div className="cur-mb-4">
          <Checkbox
            disabled={isSubmitting}
            label={acceptedTOSCheckboxLabel()}
            onChange={() => setAcceptedTOS(!acceptedTOS)}
            checked={acceptedTOS}
          />
        </div>
        <div className="cur-mb-4">
          <Checkbox
            disabled={isSubmitting}
            label="I am at least 13 years old."
            onChange={() => setIsOfAge(!isOfAge)}
            checked={isOfAge}
          />
        </div>
        <div>
          <Button
            className="cur-w-full"
            disabled={!canSubmit || isSubmitting}
            onClick={submitForm}
            type="button"
          >
            Next
          </Button>
        </div>
      </form>
    </>
  );
};
